import React, { useState } from 'react';
import { CYF_COLORS } from '../constants/cyfDefaultBranding';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { SectionHeader } from '../components/ui/section-header';
import { ComponentBox } from '../components/ui/component-box';
import { ColorPalette } from '../components/ui/color-palette';
import { ShowcaseSidebarMock } from '../components/ui/showcase-sidebar-mock';
import { InputField } from '../components/ui/input-field';
import { TextareaField } from '../components/ui/textarea-field';
import { SelectField } from '../components/ui/select-field';
import { DatePickerField } from '../components/ui/date-picker-field';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumbs';
import { EmissionBar } from '../components/ui/emission-bar';
import { MetricCard, MetricLabel, MetricValue } from '../components/ui/metric-card';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Palette, FileBarChart, Check, TriangleAlert, X, Info, Plus, Download, PlugZap, Mail, CircleAlert, CircleCheckBig, XCircle, CircleHelp, LayoutDashboard, Building2, MapPin, BarChart3, Users, CircleUser, Target, Settings, LifeBuoy, Upload, Search, Filter, Calendar, Leaf, Globe, TrendingUp, TrendingDown, Flame, Network } from 'lucide-react';

const BRAND_GREENS = [
  { name: 'Charcoal', hex: CYF_COLORS.charcoal },
  { name: 'Moss', hex: CYF_COLORS.moss },
  { name: 'Forest', hex: CYF_COLORS.forest },
  { name: 'Sage', hex: CYF_COLORS.sage },
  { name: 'Fern', hex: CYF_COLORS.fern },
  { name: 'Mist', hex: CYF_COLORS.mist },
  { name: 'Cream', hex: CYF_COLORS.cream },
  { name: 'Off-White', hex: CYF_COLORS.white },
];
const AMBER_ACCENTS = [
  { name: 'Amber', hex: CYF_COLORS.amber },
  { name: 'Amber Light', hex: CYF_COLORS.amberLight },
  { name: 'Amber Pale', hex: CYF_COLORS.amberPale },
];
const SELECT_OPTIONS = [
  { label: 'Option A', value: 'a' },
  { label: 'Option B', value: 'b' },
  { label: 'Option C', value: 'c' },
];

export function ShowcaseContent({
  switchOn,
  setSwitchOn,
  checkboxChecked,
  setCheckboxChecked,
  tabValue,
  setTabValue,
  progressValue,
  selectValue,
  setSelectValue,
  dateValue,
  setDateValue,
  sidebarBrandName = 'Compare Your Footprint',
  footerLabel = 'Compare Your Footprint · Brand System v1.0 · All components use live theme variables',
}) {
  return (
    <div className='max-w-6xl mx-auto px-16 py-14'>

          {/* ──────── COLOUR PALETTE ──────── */}
          <SectionHeader eyebrow='01 · Colour' title='Colour Palette' desc='The core brand palette. Primary greens convey sustainability, amber provides warmth and accent energy.' />

          <ColorPalette variant='brand' title='Brand Greens' items={BRAND_GREENS} />
          <ColorPalette variant='brand' title='Amber Accents' className='max-w-sm' items={AMBER_ACCENTS} />
          <ColorPalette variant='semantic' className='mb-12' />

          {/* ──────── TYPOGRAPHY ──────── */}
          <SectionHeader eyebrow='02 · Typography' title='Type System' desc='Three font families form the typographic identity. Display for headlines, body for UI text, mono for data and labels.' />

          <ComponentBox label='Font Specimens'>
            <div className='space-y-6'>
              <div>
                <div className='font-mono text-[10px] text-primary tracking-wider uppercase mb-2 flex items-center gap-2'>
                  <span className='bg-accent text-accent-foreground px-2.5 py-0.5 rounded-full text-[10px] font-bold'>DISPLAY</span>
                  DM Serif Display
                </div>
                <div className='bg-card rounded-xl p-8 border border-border shadow-sm'>
                  <div className='font-display text-[56px] text-foreground leading-none'>Measure What <em className='italic text-primary'>Matters</em></div>
                </div>
              </div>
              <div>
                <div className='font-mono text-[10px] text-primary tracking-wider uppercase mb-2 flex items-center gap-2'>
                  <span className='bg-primary text-primary-foreground px-2.5 py-0.5 rounded-full text-[10px] font-bold'>BODY</span>
                  DM Sans
                </div>
                <div className='bg-card rounded-xl p-8 border border-border shadow-sm text-foreground'>
                  <p className='text-base leading-relaxed'>DM Sans provides clean, highly legible body text across all screen sizes. Weights from 300 to 700 cover every use case — from subtle captions to bold calls to action.</p>
                  <p className='text-sm text-muted-foreground mt-3'>Secondary text uses lighter weights and muted tones to establish hierarchy without visual clutter.</p>
                </div>
              </div>
              <div>
                <div className='font-mono text-[10px] text-primary tracking-wider uppercase mb-2 flex items-center gap-2'>
                  <span className='bg-sidebar text-sidebar-primary px-2.5 py-0.5 rounded-full text-[10px] font-bold'>MONO</span>
                  Space Mono
                </div>
                <div className='bg-card rounded-xl p-8 border border-border shadow-sm'>
                  <div className='font-mono text-sm text-foreground'>{'const emissions = { scope1: 1247, scope2: 892, scope3: 8341 };'}</div>
                  <div className='font-mono text-xs text-muted-foreground mt-2'>Used for data values, table headers, labels, code snippets, and metric displays.</div>
                </div>
              </div>
            </div>
          </ComponentBox>

          {/* ──────── BUTTONS ──────── */}
          <SectionHeader eyebrow='03 · Buttons' title='Button System' desc='Primary actions use sage green. Secondary actions use outlined styles. Destructive actions are red. All buttons lift on hover.' />

          <ComponentBox label='Button Variants'>
            <div className='flex flex-wrap gap-3 mb-4'>
              <Button>Primary Action</Button>
              <Button variant='secondary'>Secondary</Button>
              <Button variant='outline'>Outline</Button>
              <Button variant='ghost'>Ghost</Button>
              <Button variant='destructive'>Destructive</Button>
              <Button variant='link'>Link Style</Button>
            </div>
            <div className='flex flex-wrap gap-3 mb-4'>
              <Button size='sm'>Small</Button>
              <Button size='default'>Default</Button>
              <Button size='lg'>Large</Button>
              <Button size='icon'><Plus size={16} strokeWidth={1.5} /></Button>
            </div>
            <div className='flex flex-wrap gap-3'>
              <Button disabled>Disabled</Button>
              <Button><Download size={16} strokeWidth={1.5} className='mr-2' /> With Icon</Button>
              <Button variant='outline'><FileBarChart size={16} strokeWidth={1.5} className='mr-2' /> Export Report</Button>
              <Button style={{ background: 'var(--secondary)', boxShadow: '0 1px 3px rgba(196,136,58,0.3)' }}>
                <PlugZap size={16} strokeWidth={1.5} className='mr-2' /> Amber Accent
              </Button>
            </div>
          </ComponentBox>

          <ComponentBox label='Button Variants — Dark Background' dark>
            <div className='flex flex-wrap gap-3'>
              <Button className='bg-transparent text-[#F5F0E8] border border-[rgba(245,240,232,0.15)] hover:bg-white/10'>Ghost Inverse</Button>
              <Button>Primary on Dark</Button>
              <Button variant='outline' className='border-[rgba(245,240,232,0.15)] text-[#F5F0E8] hover:bg-white/10'>Outline Inverse</Button>
            </div>
          </ComponentBox>

          {/* ──────── FORMS ──────── */}
          <SectionHeader eyebrow='04 · Forms' title='Form Controls' desc='Clean, accessible inputs with sage green focus states. Error states in red, success in green. All transitions at 200ms ease.' />

          <ComponentBox label='Form Elements'>
            <div className='grid grid-cols-2 gap-6'>
              <div className='space-y-4'>
                <InputField
                  label='Organisation Name'
                  placeholder='Enter organisation name...'
                  hint='This will be visible on reports.'
                />
                <InputField label='Email Address' placeholder='admin@company.com' />
                <InputField
                  label='Reporting Year'
                  placeholder='2024'
                  error='This field is required'
                />
                <TextareaField label='Notes' placeholder='Add notes about this facility...' rows={4} />
                <SelectField
                  label='Category'
                  options={SELECT_OPTIONS}
                  value={selectValue}
                  onChange={setSelectValue}
                  placeholder='Choose category...'
                  searchable
                  clearable
                />
                <DatePickerField
                  label='Reporting period start'
                  value={dateValue}
                  onChange={setDateValue}
                  placeholder='Select date'
                />
              </div>
              <div className='space-y-6'>
                <div>
                  <Label className='mb-3 block'>Notification Preferences</Label>
                  <div className='space-y-3'>
                    <div className='flex items-start gap-2.5'>
                      <Checkbox checked={checkboxChecked} onCheckedChange={setCheckboxChecked} id='cb1' />
                      <div>
                        <label htmlFor='cb1' className='text-sm font-medium text-foreground cursor-pointer'>Email notifications</label>
                        <p className='text-xs text-muted-foreground'>Receive report summaries via email</p>
                      </div>
                    </div>
                    <div className='flex items-start gap-2.5'>
                      <Checkbox id='cb2' />
                      <div>
                        <label htmlFor='cb2' className='text-sm font-medium text-foreground cursor-pointer'>Weekly digest</label>
                        <p className='text-xs text-muted-foreground'>Aggregated usage data summary</p>
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className='flex items-center justify-between'>
                  <div>
                    <Label className='block'>Dark Mode</Label>
                    <p className='text-xs text-muted-foreground mt-0.5'>Switch to dark interface theme</p>
                  </div>
                  <Switch checked={switchOn} onCheckedChange={setSwitchOn} />
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <Label className='block'>Auto-calculate Scope 3</Label>
                    <p className='text-xs text-muted-foreground mt-0.5'>Estimate indirect emissions</p>
                  </div>
                  <Switch />
                </div>
              </div>
            </div>
          </ComponentBox>

          {/* ──────── CARDS ──────── */}
          <SectionHeader eyebrow='05 · Cards' title='Card Components' desc='Cards use white backgrounds with subtle shadows. Metric cards feature a gradient top border to convey data type at a glance.' />

          <div className='grid grid-cols-3 gap-4 mb-6'>
            {[
              { label: 'Total Emissions', value: '12,847', unit: 'tCO₂e', delta: '-12.3%', down: true, gradient: 'linear-gradient(90deg, var(--primary), var(--primary))' },
              { label: 'Energy Usage', value: '2.4M', unit: 'kWh', delta: '+3.1%', down: false, gradient: 'linear-gradient(90deg, var(--secondary), var(--secondary))' },
              { label: 'Reduction Target', value: '42', unit: '%', delta: 'On track', down: true, gradient: 'linear-gradient(90deg, var(--success), var(--success))' },
            ].map(({ label, value, unit, delta, down, gradient }) => (
              <div key={label} className='bg-card rounded-2xl p-6 border border-border shadow-sm relative overflow-hidden'>
                <div className='absolute top-0 left-0 right-0 h-[3px]' style={{ background: gradient }} />
                <div className='text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5'>{label}</div>
                <div className='font-display text-4xl text-foreground leading-none mb-1.5'>
                  {value} <span className='text-lg text-muted-foreground font-sans font-normal'>{unit}</span>
                </div>
                <div className={`text-xs flex items-center gap-1 ${down ? 'text-success' : 'text-destructive'}`}>
                  {down ? <TrendingDown size={14} strokeWidth={1.5} /> : <TrendingUp size={14} strokeWidth={1.5} />} {delta}
                </div>
                <div className='mt-4 h-9 rounded bg-gradient-to-b from-primary/15 to-primary/[0.03] relative overflow-hidden'>
                  <div className='absolute bottom-0 left-0 right-0 h-0.5 rounded' style={{ background: gradient, width: '70%' }} />
                </div>
              </div>
            ))}
          </div>

          <div className='grid grid-cols-2 gap-4 mb-12'>
            <Card>
              <CardHeader>
                <CardTitle>Facilities Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm text-muted-foreground'>A standard card with header, body content, and footer. Used for grouping related information in dashboard views.</p>
              </CardContent>
              <CardFooter className='flex justify-end gap-2'>
                <Button variant='outline' size='sm'>Cancel</Button>
                <Button size='sm'>Save Changes</Button>
              </CardFooter>
            </Card>
            <Card className='overflow-hidden'>
              <CardHeader className='bg-sidebar text-sidebar-foreground rounded-t-xl'>
                <CardTitle className='text-sidebar-foreground'>Dark Header Card</CardTitle>
              </CardHeader>
              <CardContent className='pt-6'>
                <p className='text-sm text-muted-foreground'>Cards can feature dark headers for visual hierarchy. The charcoal background matches the sidebar brand tone.</p>
              </CardContent>
              <CardFooter>
                <Button variant='outline' size='sm' className='w-full'>View Details</Button>
              </CardFooter>
            </Card>
          </div>

          {/* ──────── DATA TABLE ──────── */}
          <SectionHeader eyebrow='06 · Data Display' title='Tables & Data' desc='Tables use mono font headers, cream backgrounds on thead, and subtle hover states. Data-dense layouts remain readable.' />

          <ComponentBox label='Data Table'>
            <div className='rounded-2xl overflow-hidden border border-border shadow-sm'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Facility</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className='text-right'>Scope 1 (tCO₂e)</TableHead>
                    <TableHead className='text-right'>Scope 2 (tCO₂e)</TableHead>
                    <TableHead className='text-right'>Total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { name: 'HQ London', loc: 'London, UK', s1: '1,247', s2: '892', total: '2,139', status: 'complete' },
                    { name: 'Berlin Office', loc: 'Berlin, DE', s1: '456', s2: '234', total: '690', status: 'complete' },
                    { name: 'NYC Warehouse', loc: 'New York, US', s1: '2,891', s2: '1,456', total: '4,347', status: 'pending' },
                    { name: 'Singapore Hub', loc: 'Singapore, SG', s1: '789', s2: '567', total: '1,356', status: 'draft' },
                  ].map((row) => (
                    <TableRow key={row.name} className='cursor-pointer'>
                      <TableCell className='font-semibold'>{row.name}</TableCell>
                      <TableCell className='text-muted-foreground'>{row.loc}</TableCell>
                      <TableCell className='text-right font-mono text-sm'>{row.s1}</TableCell>
                      <TableCell className='text-right font-mono text-sm'>{row.s2}</TableCell>
                      <TableCell className='text-right font-mono text-sm font-semibold'>{row.total}</TableCell>
                      <TableCell>
                        <Badge variant={row.status === 'complete' ? 'default' : row.status === 'pending' ? 'secondary' : 'outline'}>
                          {row.status === 'complete' && <Check size={10} strokeWidth={1.5} className='mr-1' />}
                          {row.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </ComponentBox>

          <ComponentBox label='Progress & Emission Bars'>
            <div className='space-y-4'>
              <EmissionBar label='Scope 1 — Direct' value={34} displayValue='34%' scope='scope1' />
              <EmissionBar label='Scope 2 — Energy' value={22} displayValue='22%' scope='scope2' />
              <EmissionBar label='Scope 3 — Indirect' value={44} displayValue='44%' scope='scope3' />
            </div>
            <Separator className='my-6' />
            <div>
              <div className='text-sm font-medium text-foreground mb-2'>Overall Reduction Progress</div>
              <Progress value={progressValue} className='h-3' />
              <div className='font-mono text-xs text-muted-foreground mt-1'>{progressValue}% of 2030 target</div>
            </div>
            <Separator className='my-6' />
            <div className='text-sm font-medium text-foreground mb-2'>Metric Cards</div>
            <div className='grid grid-cols-3 gap-4'>
              <MetricCard accent='default'>
                <MetricLabel>Total Emissions</MetricLabel>
                <MetricValue unit='tCO₂e'>1,247</MetricValue>
              </MetricCard>
              <MetricCard accent='amber'>
                <MetricLabel>Target</MetricLabel>
                <MetricValue unit='%'>67</MetricValue>
              </MetricCard>
              <MetricCard accent='success'>
                <MetricLabel>Reduction</MetricLabel>
                <MetricValue unit='%'>12</MetricValue>
              </MetricCard>
            </div>
          </ComponentBox>

          {/* ──────── BADGES ──────── */}
          <SectionHeader eyebrow='07 · Badges & Status' title='Badges & Labels' desc='Pill-shaped badges communicate status at a glance. Semantic colours align with alert states. Dot indicators show live status.' />

          <ComponentBox label='Badge Variants'>
            <div className='flex flex-wrap gap-2 mb-4'>
              <Badge>Default</Badge>
              <Badge variant='secondary'>Secondary</Badge>
              <Badge variant='outline'>Outline</Badge>
              <Badge variant='destructive'>Destructive</Badge>
            </div>
            <div className='flex flex-wrap gap-2 mb-4'>
              <span className='inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[var(--success-bg)] text-[var(--success)]'>
                <span className='text-[7px]'>●</span> Complete
              </span>
              <span className='inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[var(--warning-bg)] text-[var(--warning)]'>
                <span className='text-[7px]'>●</span> Pending
              </span>
              <span className='inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[var(--danger-bg)] text-[var(--danger)]'>
                <span className='text-[7px]'>●</span> Overdue
              </span>
              <span className='inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[var(--info-bg)] text-[var(--info)]'>
                <span className='text-[7px]'>●</span> In Review
              </span>
              <span className='inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-sidebar text-sidebar-primary'>
                Draft
              </span>
            </div>
          </ComponentBox>

          {/* ──────── ALERTS ──────── */}
          <SectionHeader eyebrow='08 · Feedback' title='Alerts & Notifications' desc='Alerts use semantic background tints with matching icon colours. Clean, scannable, non-intrusive.' />

          <div className='space-y-3 mb-12'>
            <Alert className='border-success/30 bg-success-bg'>
              <CircleCheckBig size={18} strokeWidth={1.5} className='text-[var(--success)]' />
              <AlertTitle className='text-[var(--success)]'>Report Published</AlertTitle>
              <AlertDescription>Your 2024 carbon footprint report has been generated and is ready for download.</AlertDescription>
            </Alert>
            <Alert className='border-warning/30 bg-warning-bg'>
              <TriangleAlert size={18} strokeWidth={1.5} className='text-[var(--warning)]' />
              <AlertTitle className='text-[var(--warning)]'>Incomplete Data</AlertTitle>
              <AlertDescription>3 facilities are missing Scope 2 electricity data for Q4 2024.</AlertDescription>
            </Alert>
            <Alert variant='destructive'>
              <XCircle size={18} strokeWidth={1.5} />
              <AlertTitle>Submission Failed</AlertTitle>
              <AlertDescription>Unable to process the usage data import. Please check the file format and try again.</AlertDescription>
            </Alert>
            <Alert className='border-info/30 bg-info-bg'>
              <Info size={18} strokeWidth={1.5} className='text-[var(--info)]' />
              <AlertTitle className='text-[var(--info)]'>New Feature</AlertTitle>
              <AlertDescription>Science-Based Targets module is now available. Enable it from Settings.</AlertDescription>
            </Alert>
          </div>

          {/* ──────── TABS ──────── */}
          <SectionHeader eyebrow='09 · Navigation' title='Tabs & Breadcrumbs' desc='Tab navigation uses an underline indicator. Breadcrumbs provide wayfinding context across report hierarchies.' />

          <ComponentBox label='Tabs'>
            <Tabs value={tabValue} onValueChange={setTabValue}>
              <TabsList className='bg-transparent border-b border-border rounded-none p-0 h-auto'>
                {['overview', 'emissions', 'facilities', 'targets'].map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className='rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-2.5 capitalize text-sm'>
                    {tab}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value='overview' className='pt-6'>
                <p className='text-sm text-muted-foreground'>Overview tab content showing dashboard-level summary data and key metrics.</p>
              </TabsContent>
              <TabsContent value='emissions' className='pt-6'>
                <p className='text-sm text-muted-foreground'>Detailed emissions breakdown by scope, category, and facility.</p>
              </TabsContent>
              <TabsContent value='facilities' className='pt-6'>
                <p className='text-sm text-muted-foreground'>Facility-level data with individual carbon footprint breakdowns.</p>
              </TabsContent>
              <TabsContent value='targets' className='pt-6'>
                <p className='text-sm text-muted-foreground'>Science-based reduction targets and progress tracking.</p>
              </TabsContent>
            </Tabs>
          </ComponentBox>

          <ComponentBox label='Breadcrumbs'>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href='#'>Home</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href='#'>Reports</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href='#'>2024/2025</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Consumption Report</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </ComponentBox>

          {/* ──────── TOOLTIPS & MODALS ──────── */}
          <SectionHeader eyebrow='10 · Overlays' title='Tooltips & Modals' desc='Charcoal tooltips with cream text. Modals use generous 24px radius with backdrop blur.' />

          <ComponentBox label='Tooltips'>
            <div className='flex gap-4'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant='outline'>Hover for Tooltip</Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This is a branded tooltip</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant='ghost' size='icon'><CircleHelp size={16} strokeWidth={1.5} /></Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Help information appears here</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </ComponentBox>

          <ComponentBox label='Modal Dialog'>
            <Dialog>
              <DialogTrigger asChild>
                <Button>Open Modal</Button>
              </DialogTrigger>
              <DialogContent className='sm:max-w-[480px]'>
                <DialogHeader>
                  <DialogTitle className='font-display text-xl'>Confirm Submission</DialogTitle>
                </DialogHeader>
                <div className='py-4'>
                  <p className='text-sm text-muted-foreground leading-relaxed'>
                    You are about to submit the 2024 carbon footprint report for "London HQ". This action will make the data available for external verification.
                  </p>
                </div>
                <DialogFooter className='gap-2'>
                  <Button variant='outline'>Cancel</Button>
                  <Button>Submit Report</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </ComponentBox>

          {/* ──────── ICONOGRAPHY ──────── */}
          <SectionHeader eyebrow='11 · Iconography' title='Icon System' desc='Lucide React line icons with strokeWidth 1.5. Consistent 16px sizing in sidebar, 20-24px in content areas. Scope icons use brand-tinted backgrounds.' />

          <ComponentBox label='Core Platform Icons'>
            <div className='flex flex-wrap gap-3'>
              {[
                { name: 'Dashboard', icon: <LayoutDashboard size={20} strokeWidth={1.5} /> },
                { name: 'Organisation', icon: <Building2 size={20} strokeWidth={1.5} /> },
                { name: 'Facility', icon: <MapPin size={20} strokeWidth={1.5} /> },
                { name: 'Usage Data', icon: <BarChart3 size={20} strokeWidth={1.5} /> },
                { name: 'Reports', icon: <FileBarChart size={20} strokeWidth={1.5} /> },
                { name: 'Users', icon: <Users size={20} strokeWidth={1.5} /> },
                { name: 'Profile', icon: <CircleUser size={20} strokeWidth={1.5} /> },
                { name: 'Targets', icon: <Target size={20} strokeWidth={1.5} /> },
                { name: 'Settings', icon: <Settings size={20} strokeWidth={1.5} /> },
                { name: 'Support', icon: <LifeBuoy size={20} strokeWidth={1.5} /> },
                { name: 'Download', icon: <Download size={20} strokeWidth={1.5} /> },
                { name: 'Upload', icon: <Upload size={20} strokeWidth={1.5} /> },
                { name: 'Search', icon: <Search size={20} strokeWidth={1.5} /> },
                { name: 'Filter', icon: <Filter size={20} strokeWidth={1.5} /> },
                { name: 'Calendar', icon: <Calendar size={20} strokeWidth={1.5} /> },
                { name: 'Leaf', icon: <Leaf size={20} strokeWidth={1.5} /> },
                { name: 'Globe', icon: <Globe size={20} strokeWidth={1.5} /> },
                { name: 'Trend Up', icon: <TrendingUp size={20} strokeWidth={1.5} /> },
                { name: 'Trend Down', icon: <TrendingDown size={20} strokeWidth={1.5} /> },
                { name: 'Check', icon: <CircleCheckBig size={20} strokeWidth={1.5} /> },
              ].map(({ name, icon }) => (
                <div key={name} className='bg-card border border-border rounded-xl px-3.5 py-4 flex flex-col items-center gap-2.5 min-w-[84px] shadow-sm hover:border-muted hover:shadow hover:-translate-y-px transition-all cursor-default'>
                  <span className='text-primary'>{icon}</span>
                  <span className='font-mono text-[9px] text-muted-foreground text-center leading-tight'>{name}</span>
                </div>
              ))}
            </div>
          </ComponentBox>

          <ComponentBox label='Scope Icons'>
            <div className='flex gap-3'>
              {[
                { name: 'Scope 1\nDirect', icon: <Flame size={24} strokeWidth={1.5} />, color: CYF_COLORS.charcoal },
                { name: 'Scope 2\nEnergy', icon: <PlugZap size={24} strokeWidth={1.5} />, color: CYF_COLORS.forest },
                { name: 'Scope 3\nIndirect', icon: <Network size={24} strokeWidth={1.5} />, color: CYF_COLORS.sage },
              ].map(({ name, icon, color }) => (
                <div key={name} className='border border-primary/25 bg-primary/[0.03] rounded-xl px-5 py-4 flex flex-col items-center gap-2.5 min-w-[100px]'>
                  <span style={{ color }}>{icon}</span>
                  <span className='font-mono text-[9px] text-muted-foreground text-center leading-tight whitespace-pre-line'>{name}</span>
                </div>
              ))}
            </div>
          </ComponentBox>

          {/* ──────── SIDEBAR PREVIEW ──────── */}
          <SectionHeader eyebrow='12 · App Preview' title='Sidebar & Navigation' desc='Dark charcoal sidebar with fern-green active indicators. Section labels use mono font at 9px uppercase.' />

          <ShowcaseSidebarMock brandName={sidebarBrandName} showMetrics />

          {/* ──────── CHART PALETTE ──────── */}
          <SectionHeader eyebrow='13 · Charts' title='Chart Colour Palette' desc='Five chart colours designed for data visualization. Distinct enough for accessibility, harmonious within the brand system.' />

          <div className='flex gap-3 mb-12'>
            {[
              { name: 'Chart 1', hex: CYF_COLORS.sage, var: '--chart-1' },
              { name: 'Chart 2', hex: CYF_COLORS.amber, var: '--chart-2' },
              { name: 'Chart 3', hex: CYF_COLORS.moss, var: '--chart-3' },
              { name: 'Chart 4', hex: CYF_COLORS.fern, var: '--chart-4' },
              { name: 'Chart 5', hex: CYF_COLORS.forest, var: '--chart-5' },
            ].map(({ name, hex }) => (
              <div key={name} className='flex-1'>
                <div className='h-16 rounded-xl' style={{ background: hex }} />
                <div className='text-center mt-2'>
                  <div className='font-mono text-[10px] text-muted-foreground'>{name}</div>
                  <div className='font-mono text-[10px] text-muted-foreground'>{hex}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ──────── SPACING ──────── */}
          <SectionHeader eyebrow='14 · Spacing' title='Spacing Scale' desc='4px base unit system. Consistent spacing creates visual rhythm.' />

          <div className='flex flex-wrap gap-3 items-end mb-12'>
            {[
              { label: 'space-1', px: 4 },
              { label: 'space-2', px: 8 },
              { label: 'space-3', px: 12 },
              { label: 'space-4', px: 16 },
              { label: 'space-6', px: 24 },
              { label: 'space-8', px: 32 },
              { label: 'space-10', px: 40 },
              { label: 'space-12', px: 48 },
              { label: 'space-16', px: 64 },
            ].map(({ label, px }) => (
              <div key={label} className='flex flex-col items-center gap-1.5'>
                <div className='bg-primary rounded-sm' style={{ width: px, height: px }} />
                <span className='font-mono text-[9px] text-muted-foreground whitespace-nowrap'>{label}<br />{px}px</span>
              </div>
            ))}
          </div>

          {/* ──────── MOTION ──────── */}
          <SectionHeader eyebrow='15 · Motion' title='Motion Principles' desc='Purposeful, not decorative. 200ms ease for hover, 400ms spring for confirms. Always honour prefers-reduced-motion.' />

          <div className='grid grid-cols-3 gap-4 mb-12'>
            {[
              { title: 'Translate — Ease', desc: 'Hover lifts, drawer slides, tooltip appear', spec: '200ms ease · translateY(-4px)', hoverClass: 'hover:-translate-y-1 transition-transform duration-200' },
              { title: 'Scale — Spring', desc: 'Button presses, success states', spec: '400ms cubic-bezier(0.34, 1.56, 0.64, 1)', hoverClass: 'hover:scale-110 transition-transform duration-400', amber: true },
              { title: 'Fade — Linear', desc: 'Overlays, loading states', spec: '200ms linear · opacity', hoverClass: 'hover:opacity-30 transition-opacity duration-200', dark: true },
            ].map(({ title, desc, spec, hoverClass, amber, dark: isDark }) => (
              <div key={title} className='bg-card rounded-2xl p-6 text-center border border-border shadow-sm overflow-hidden'>
                <div className={`w-12 h-12 rounded-lg mx-auto mb-4 ${hoverClass}`} style={{ background: amber ? CYF_COLORS.amber : isDark ? CYF_COLORS.charcoal : CYF_COLORS.sage }} />
                <h4 className='text-sm font-semibold text-foreground mb-1'>{title}</h4>
                <p className='text-xs text-muted-foreground leading-relaxed'>{desc}</p>
                <div className='font-mono text-[10px] text-primary mt-2'>{spec}</div>
              </div>
            ))}
          </div>

          {/* ──────── MORE COMPONENTS ──────── */}
          <SectionHeader eyebrow='16 · More' title='Pagination, Skeleton, Sheet, Radio' desc='Additional shared UI components used across the app. Use these for lists, loading states, side panels, and single-choice options.' />

          <div className='grid grid-cols-2 gap-6 mb-12'>
            <ComponentBox label='Pagination'>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious href='#' />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href='#'>1</PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href='#' isActive>2</PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href='#'>3</PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext href='#' />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </ComponentBox>
            <ComponentBox label='Skeleton'>
              <div className='space-y-3'>
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-4/5' />
                <Skeleton className='h-4 w-3/5' />
                <Skeleton className='h-20 w-full' />
              </div>
            </ComponentBox>
            <ComponentBox label='Sheet (slide-out panel)'>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='outline'>Open Sheet</Button>
                </SheetTrigger>
                <SheetContent side='right'>
                  <SheetHeader>
                    <SheetTitle>Panel Title</SheetTitle>
                  </SheetHeader>
                  <p className='text-sm text-muted-foreground pt-4'>Sheet content slides in from the right. Use for filters, details, or secondary actions.</p>
                </SheetContent>
              </Sheet>
            </ComponentBox>
            <ComponentBox label='Radio Group'>
              <RadioGroup defaultValue='option-a' className='gap-3'>
                <div className='flex items-center gap-2'>
                  <RadioGroupItem value='option-a' id='option-a' />
                  <Label htmlFor='option-a'>Option A</Label>
                </div>
                <div className='flex items-center gap-2'>
                  <RadioGroupItem value='option-b' id='option-b' />
                  <Label htmlFor='option-b'>Option B</Label>
                </div>
                <div className='flex items-center gap-2'>
                  <RadioGroupItem value='option-c' id='option-c' />
                  <Label htmlFor='option-c'>Option C</Label>
                </div>
              </RadioGroup>
            </ComponentBox>
          </div>

          {/* Footer */}
          <div className='text-center py-12 border-t border-border'>
            <div className='font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground'>
              {footerLabel}
            </div>
          </div>
    </div>
  );
}

const BrandShowcase = () => {
  const [switchOn, setSwitchOn] = useState(true);
  const [checkboxChecked, setCheckboxChecked] = useState(true);
  const [tabValue, setTabValue] = useState('overview');
  const [progressValue] = useState(67);
  const [selectValue, setSelectValue] = useState(null);
  const [dateValue, setDateValue] = useState(null);

  return (
    <TooltipProvider>
      <div className='min-h-screen' style={{ background: 'var(--background)' }}>
        {/* Hero Header */}
        <div className='relative overflow-hidden' style={{ background: `linear-gradient(135deg, ${CYF_COLORS.charcoal} 0%, ${CYF_COLORS.moss} 100%)` }}>
          <div className='absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full' style={{ background: `radial-gradient(circle, rgba(92,140,110,0.25) 0%, transparent 70%)` }} />
          <div className='absolute -bottom-16 left-[300px] w-[280px] h-[280px] rounded-full' style={{ background: `radial-gradient(circle, rgba(196,136,58,0.12) 0%, transparent 70%)` }} />
          <div className='relative z-10 px-16 py-16 max-w-6xl'>
            <div className='font-mono text-[11px] tracking-[0.18em] uppercase text-[#8BB89A] mb-3'>Brand System Preview</div>
            <h1 className='font-display text-5xl text-[#F5F0E8] leading-tight mb-4'>
              Compare<em className='italic text-[#8BB89A]'>Your</em>Footprint
            </h1>
            <p className='text-base text-[rgba(245,240,232,0.65)] max-w-lg leading-relaxed'>
              Complete UI component showcase built from the brand guidelines. Every element below uses the live theme variables.
            </p>
            <div className='flex gap-4 mt-8 flex-wrap'>
              <span className='flex items-center gap-2 bg-white/[0.08] border border-white/[0.12] px-4 py-1.5 rounded-full text-xs text-[#C8DDD1]'>
                <Palette size={14} strokeWidth={1.5} /> Design System v1.0
              </span>
              <span className='flex items-center gap-2 bg-white/[0.08] border border-white/[0.12] px-4 py-1.5 rounded-full text-xs text-[#C8DDD1]'>
                <FileBarChart size={14} strokeWidth={1.5} /> All Components
              </span>
              <span className='flex items-center gap-2 bg-white/[0.08] border border-white/[0.12] px-4 py-1.5 rounded-full text-xs text-[#C8DDD1]'>
                <Check size={14} strokeWidth={1.5} /> Live Theme
              </span>
            </div>
          </div>
        </div>

        <ShowcaseContent
          switchOn={switchOn}
          setSwitchOn={setSwitchOn}
          checkboxChecked={checkboxChecked}
          setCheckboxChecked={setCheckboxChecked}
          tabValue={tabValue}
          setTabValue={setTabValue}
          progressValue={progressValue}
          selectValue={selectValue}
          setSelectValue={setSelectValue}
          dateValue={dateValue}
          setDateValue={setDateValue}
        />
      </div>
    </TooltipProvider>
  );
};

export default BrandShowcase;
